import React, {
  useCallback,
  useEffect,
  useRef,
  memo,
  useState,
  useMemo
} from 'react';
import TimelineWrapper from './TimelineWrapper';
import { useDispatch } from 'react-redux';
import { deleteActivity, updateActivityName } from 'store/config/activities';
import { useConfirm } from 'plugins/confirm';
import ErrorIcon from '@mui/icons-material/Error';
import { IconButton, Skeleton, Tooltip } from '@mui/material';
import { DeleteSweep as DeleteSweepIcon } from '@mui/icons-material';
import { TimelineState } from '@xzdarcy/react-timeline-editor';
import { useSnack } from 'plugins/snack';
import { useSelector } from 'react-redux';
import { CustomRender0 } from './timelineRenderer';
import _ from 'lodash';

interface TimelineScaleProps {
  activityData: any;
  domRef: React.RefObject<HTMLDivElement>;
  timelineState: React.RefObject<TimelineState>;
  activityDataPointer: number;
  setActivityData: React.Dispatch<any>;
  setDeleted: React.Dispatch<React.SetStateAction<boolean>>;
  setActivityDataPointer: React.Dispatch<React.SetStateAction<number>>;
  setCropDisable: React.Dispatch<React.SetStateAction<boolean>>;
  videoRef: React.RefObject<HTMLVideoElement>;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  setCropState: React.Dispatch<
    React.SetStateAction<{
      action_id: string;
      row_id: string;
      start: number;
    }>
  >;
  handleUpdateClip: ({
    activityId,
    clipId,
    start,
    end
  }: {
    activityId: string;
    clipId: string;
    start: number;
    end: number;
  }) => void;
  timelineRef: React.RefObject<HTMLDivElement>;
  duration: number;
  scale: number;
  zoomCount: number;
  getCropState: {
    action_id: string;
    row_id: string;
    start: number;
  };
  timelineWrapperRef: React.RefObject<HTMLDivElement>;
  scaleWidth: number;
  timelineBackground: string;
  handleBackward: () => void;
  syncActivity: (activityData: any) => Promise<void>;
  handleForward: () => void;
}

function TimelineScale({
  handleForward,
  syncActivity,
  handleBackward,
  activityData,
  domRef,
  timelineState,
  setDeleted,
  activityDataPointer,
  setActivityData,
  setActivityDataPointer,
  setCropDisable,
  videoRef,
  setIsPlaying,
  setCropState,
  handleUpdateClip,
  timelineRef,
  duration,
  scale,
  zoomCount,
  getCropState,
  timelineWrapperRef,
  scaleWidth,
  timelineBackground
}: TimelineScaleProps) {
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLDivElement>(null);
  const [activityName, setActivityName] = useState<any>('');
  const snack = useSnack();
  const allActivities = useSelector((state: any) => state?.activities?.data);
  const confirm = useConfirm();

  const handleDeleteActivityClick = (activityId: any) => {
    confirm({
      title: (
        <div
          style={{
            display: 'flex',
            alignContent: 'center',
            alignItems: 'center'
          }}
        >
          <div style={{ marginRight: '10px' }}>Delete Activity</div>
          <ErrorIcon />
        </div>
      ),
      content:
        'Deleting this activity will delete it from all videos in this configuration. This action cannot be undone. Are you sure you want to delete this activity?'
    }).then(() => {
      const deletedData = activityData[activityDataPointer].filter(
        (activity: any) => activity.id !== activityId
      );
      dispatch(deleteActivity({ activityId }));
      setActivityData([]);
      setActivityDataPointer(0);
      setDeleted(true);
    });
  };

  // Create debounced version of getCropState
  const [debouncedCropState, setDebouncedCropState] = useState(getCropState);

  // Create a debounced function that updates the state
  const debouncedSetState = useMemo(
    () =>
      _.debounce((newState: any) => {
        setDebouncedCropState(newState);
      }, 100), // 100ms debounce time
    []
  );

  // Update debounced state when getCropState changes
  useEffect(() => {
    debouncedSetState(getCropState);
  }, [getCropState, debouncedSetState]);

  // Use the debounced state in the render function
  const getActionRender = useCallback(
    (action: any, row: any) => {
      return (
        <CustomRender0
          setActivityData={setActivityData}
          setActivityDataPointer={setActivityDataPointer}
          action={action}
          start={Number(action.start)}
          end={Number(action.end)}
          selected={
            debouncedCropState?.row_id === row.id &&
            debouncedCropState?.action_id === action?.id
          }
          row={row}
          width={timelineWrapperRef.current?.clientWidth || 0}
        />
      );
    },
    [
      setActivityData,
      setActivityDataPointer,
      debouncedCropState,
      timelineWrapperRef
    ]
  );

  // Memoize the keyboard event handler
  const fwdBckwd = useCallback(
    (event: any) => {
      if (event.ctrlKey && event.key === 's') {
        event.preventDefault();
        return syncActivity(activityData[activityDataPointer]).then(() => {
          snack({
            message: 'Activities saved successfully',
            severity: 'success'
          });
        });
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        return handleBackward();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        return handleForward();
      }
    },
    [
      syncActivity,
      activityData,
      activityDataPointer,
      snack,
      handleBackward,
      handleForward
    ]
  );

  // Memoize the scroll sync function
  const syncScroll = useCallback(() => {
    if (domRef.current && timelineWrapperRef.current) {
      domRef.current.scrollTop = timelineWrapperRef.current.scrollTop;
    }
  }, [domRef, timelineWrapperRef]);

  // Event listeners effect
  useEffect(() => {
    document.addEventListener('keydown', fwdBckwd);
    if (timelineWrapperRef.current) {
      timelineWrapperRef.current.addEventListener('scroll', syncScroll);
    }

    return () => {
      if (timelineWrapperRef.current) {
        timelineWrapperRef.current.removeEventListener('scroll', syncScroll);
      }
      document.removeEventListener('keydown', fwdBckwd);
    };
  }, [fwdBckwd, syncScroll, timelineWrapperRef]);

  // Memoize the scroll handler
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      timelineState.current?.setScrollTop(target.scrollTop);
    },
    [timelineState]
  );

  // Memoize the activity input change handler
  const handleActivityNameChange = useCallback(
    (a: any, value: string) => {
      dispatch(
        updateActivityName({
          name: value || '',
          ActivityId: a.id
        })
      );
    },
    [dispatch]
  );
  // Render the component
  return scale > 0 ? (
    <div
      className="timeline-editor-example7"
      style={{
        maxHeight: 160,
        overflowY: 'scroll'
      }}
    >
      <div
        ref={domRef}
        style={{
          overflow: 'overlay'
        }}
        onScroll={handleScroll}
        className={'timeline-list'}
      >
        {activityData[activityDataPointer]?.map((a: any) => (
          <div className="timeline-list-item" key={a.id} ref={inputRef}>
            <Tooltip title={a.activity} placement="top-start">
              <div className="timeline-list-item-action">
                <input
                  style={{
                    backgroundColor:
                      a.activity === activityName?.data?.name
                        ? 'black'
                        : '#ededed',
                    color:
                      a.activity === activityName?.data?.name
                        ? '#ededed'
                        : 'black'
                  }}
                  type="text"
                  className="text"
                  value={a.activity}
                  onKeyDown={event => event.stopPropagation()}
                  onChange={e => handleActivityNameChange(a, e.target.value)}
                />
                <IconButton
                  disableRipple
                  style={{
                    padding: 0
                  }}
                  onClick={() => {
                    handleDeleteActivityClick(a.id);
                  }}
                >
                  <DeleteSweepIcon
                    style={{
                      cursor: 'pointer'
                    }}
                  />
                </IconButton>
              </div>
            </Tooltip>
          </div>
        ))}
      </div>

      {activityData[activityDataPointer]?.length > 0 && (
        <TimelineWrapper
          setCropDisable={setCropDisable}
          videoRef={videoRef}
          setActivityName={setActivityName}
          setIsPlaying={setIsPlaying}
          setCropState={setCropState}
          handleUpdateClip={handleUpdateClip}
          timelineRef={timelineRef}
          duration={duration}
          scale={scale}
          zoomCount={zoomCount}
          getActionRender={getActionRender}
          timelineWrapperRef={timelineWrapperRef}
          scaleWidth={scaleWidth}
          timelineBackground={timelineBackground}
          activityData={activityData}
          activityDataPointer={activityDataPointer}
          domRef={domRef}
          timelineState={timelineState}
          setActivityData={setActivityData}
          setActivityDataPointer={setActivityDataPointer}
        />
      )}
    </div>
  ) : (
    <div style={{ height: '500px', padding: '10px' }}>
      <Skeleton
        style={{ margin: '10px' }}
        variant="rectangular"
        width={'100%'}
        height={'15%'}
      />
      <Skeleton
        style={{ margin: '10px' }}
        variant="rectangular"
        width={'100%'}
        height={'15%'}
      />
      <Skeleton
        style={{ margin: '10px' }}
        variant="rectangular"
        width={'100%'}
        height={'15%'}
      />
      <Skeleton
        style={{ margin: '10px' }}
        variant="rectangular"
        width={'100%'}
        height={'15%'}
      />
    </div>
  );
}

export default memo(TimelineScale);
